// Mock/demo data for STICKY Launchpad Phase 1 (UI-only)
// All values are placeholders. No blockchain integration.

/** @typedef {("planned"|"demo"|"live"|"ended")} LaunchStatus */

/** @typedef {Object} Creator
 *  @property {string} handle
 *  @property {?string} avatar
 */

/** @typedef {Object} Token
 *  @property {string} slug
 *  @property {string} name
 *  @property {string} symbol
 *  @property {string} description
 *  @property {string} logo   // emoji or img URL
 *  @property {number} supply
 *  @property {number} decimals
 *  @property {{ x?: string, telegram?: string, website?: string, discord?: string }} socials
 *  @property {?string} contract
 */

/** @typedef {Object} Launch
 *  @property {string} id
 *  @property {Token} token
 *  @property {Creator} creator
 *  @property {LaunchStatus} status
 *  @property {number} marketCapUsd
 *  @property {number} volumeUsd
 *  @property {number} holders
 *  @property {string} launchedAt  // ISO date
 *  @property {number} priceUsd
 *  @property {number} liquidityUsd
 */

export const DEMO_LAUNCHES = [
  {
    id: "sticky-demo-1",
    token: {
      slug: "demo",
      name: "Example Token",
      symbol: "EXAMPLE",
      description: "A community-first Solana token launched through STICKY Launchpad. This is preview data only.",
      logo: "🚀",
      supply: 1_000_000_000,
      decimals: 6,
      socials: { x: "https://x.com/example", telegram: "https://t.me/example", website: "https://example.com" },
      contract: "DemoTokenContract1111111111111111111111111",
    },
    creator: { handle: "sticky_creator", avatar: null },
    status: "demo",
    marketCapUsd: 128_450,
    volumeUsd: 42_180,
    holders: 312,
    launchedAt: "2026-02-10T14:22:00Z",
    priceUsd: 0.000128,
    liquidityUsd: 24_500,
  },
  {
    id: "sticky-demo-2",
    token: {
      slug: "meme-vault",
      name: "MemeVault",
      symbol: "MVLT",
      description: "Powered by community meme votes. Demo listing.",
      logo: "🎭",
      supply: 500_000_000,
      decimals: 6,
      socials: { x: "https://x.com/memevault" },
      contract: null,
    },
    creator: { handle: "meme_dao", avatar: null },
    status: "demo",
    marketCapUsd: 86_120,
    volumeUsd: 15_640,
    holders: 214,
    launchedAt: "2026-02-08T09:12:00Z",
    priceUsd: 0.000172,
    liquidityUsd: 12_800,
  },
  {
    id: "sticky-demo-3",
    token: {
      slug: "aurum-pups",
      name: "Aurum Pups",
      symbol: "PUPS",
      description: "Golden pups on Solana. Demo listing for preview only.",
      logo: "🐕",
      supply: 750_000_000,
      decimals: 6,
      socials: { x: "https://x.com/aurumpups", website: "https://aurumpups.io" },
      contract: null,
    },
    creator: { handle: "aurum_labs", avatar: null },
    status: "demo",
    marketCapUsd: 254_900,
    volumeUsd: 78_400,
    holders: 502,
    launchedAt: "2026-02-05T18:04:00Z",
    priceUsd: 0.00034,
    liquidityUsd: 41_200,
  },
  {
    id: "sticky-demo-4",
    token: {
      slug: "sol-toast",
      name: "SolToast",
      symbol: "TOAST",
      description: "Toast the community. Demo card.",
      logo: "🍞",
      supply: 2_000_000_000,
      decimals: 9,
      socials: { telegram: "https://t.me/soltoast" },
      contract: null,
    },
    creator: { handle: "toast_master", avatar: null },
    status: "demo",
    marketCapUsd: 42_060,
    volumeUsd: 8_240,
    holders: 118,
    launchedAt: "2026-02-11T22:33:00Z",
    priceUsd: 0.0000210,
    liquidityUsd: 6_400,
  },
  {
    id: "sticky-demo-5",
    token: {
      slug: "sky-cat",
      name: "SkyCat",
      symbol: "SKY",
      description: "The cat who watches Solana. Preview only.",
      logo: "🐈",
      supply: 1_000_000_000,
      decimals: 6,
      socials: { x: "https://x.com/skycat", discord: "https://discord.gg/skycat" },
      contract: null,
    },
    creator: { handle: "sky_labs", avatar: null },
    status: "demo",
    marketCapUsd: 511_200,
    volumeUsd: 132_450,
    holders: 894,
    launchedAt: "2026-02-01T12:00:00Z",
    priceUsd: 0.000511,
    liquidityUsd: 85_600,
  },
  {
    id: "sticky-demo-6",
    token: {
      slug: "orbit-dog",
      name: "OrbitDog",
      symbol: "ORBIT",
      description: "Orbiting the meta since 2026. Demo entry.",
      logo: "🛸",
      supply: 888_000_000,
      decimals: 6,
      socials: {},
      contract: null,
    },
    creator: { handle: "orbit_dao", avatar: null },
    status: "demo",
    marketCapUsd: 19_800,
    volumeUsd: 3_120,
    holders: 74,
    launchedAt: "2026-02-12T06:45:00Z",
    priceUsd: 0.0000223,
    liquidityUsd: 3_100,
  },
];

export const LAUNCH_PACKAGE = {
  priceUsd: 99,
  splits: [
    { label: "STICKY Ecosystem",     pct: 40 },
    { label: "STICKY Buy & Burn",    pct: 40 },
    { label: "Launchpad Operations", pct: 20 },
  ],
  includes: [
    "Solana token launch infrastructure",
    "STICKY Launchpad project page",
    "Social links",
    "Contract address page",
    "Market information",
    '"Launched on STICKY Launchpad" badge',
  ],
};

export const LAUNCHPAD_FAQ = [
  {
    q: "What is STICKY Launchpad?",
    a: "A future STICKY Ecosystem platform designed to help creators launch Solana tokens.",
  },
  {
    q: "How much will it cost?",
    a: "The planned Launch Package is $99.",
  },
  {
    q: "Is STICKY Launchpad live?",
    a: "No. It is currently under development.",
  },
  {
    q: "Can I launch a token now?",
    a: "No. The current version is a preview.",
  },
  {
    q: "What happens to the launch fee?",
    a: "The planned allocation is 40% STICKY Ecosystem, 40% STICKY Buy & Burn, and 20% Launchpad Operations.",
  },
  {
    q: "Will I need a Solana wallet?",
    a: "Yes. The final Launchpad will require a compatible Solana wallet.",
  },
];

export const BUY_BURN_STATS = {
  stickyBurned: 0,
  launches: 0,
  buyBurnAllocationUsd: 0,
};

export const ECOSYSTEM_STATS = {
  ecosystemAllocationUsd: 0,
  launches: 0,
};

export const HOW_IT_WORKS = [
  { n: "01", title: "Create",  desc: "Create your token." },
  { n: "02", title: "Connect", desc: "Connect your Solana wallet." },
  { n: "03", title: "Launch",  desc: "Launch through STICKY Launchpad." },
  { n: "04", title: "Grow",    desc: "Share your project with your community." },
];
